import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingBag,
  Utensils,
  BarChart3,
  Wallet,
  Users,
  Settings,
  Gift,
  ChefHat,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  Megaphone,
  Check,
  Clock,
  ShoppingBasket
} from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useRestaurantAuth } from '../../contexts/RestaurantAuthContext';
import { getRestaurantDishes, getRestaurantOrders } from '../../services/restaurantApi';

const SEARCH_RESULT_LIMIT = 5;
const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const RestaurantLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState({ dishes: [], orders: [] });
  const location = useLocation();
  const navigate = useNavigate();
  const { logout: contextLogout, restaurantData } = useRestaurantAuth();

  // Recherche globale : interroge les plats et commandes du restaurant connecté
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 2) {
      setSearchResults({ dishes: [], orders: [] });
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const [dishes, orders] = await Promise.all([
          getRestaurantDishes().catch(() => []),
          getRestaurantOrders().catch(() => []),
        ]);
        const dishList = Array.isArray(dishes) ? dishes : dishes?.items || [];
        const orderList = Array.isArray(orders) ? orders : orders?.items || [];

        setSearchResults({
          dishes: dishList
            .filter((d) => d.name?.toLowerCase().includes(term))
            .slice(0, SEARCH_RESULT_LIMIT),
          orders: orderList
            .filter((o) => String(o.id ?? o.order_number ?? '').toLowerCase().includes(term))
            .slice(0, SEARCH_RESULT_LIMIT),
        });
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const goToSearchResult = (path) => {
    navigate(path);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const hasSearchResults = searchResults.dishes.length > 0 || searchResults.orders.length > 0;

  // Hook pour les notifications
  const {
    notifications,
    unreadCount,
    countsByCategory,
    loading: notificationsLoading,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const byCat = countsByCategory?.by_category || {};
  const pendingOrdersCount = byCat.order ?? 0;
  const restaurantInitial = (restaurantData?.name || 'R').trim().charAt(0).toUpperCase();
  const accountName = restaurantData?.name || 'Mon restaurant';
  const accountEmail = localStorage.getItem('restaurantEmail') || restaurantData?.email || '';
  // Badge par onglet selon le contexte des notifications (order, review, catering, promotion, complaint, general, favorite)
  const getBadgeForPath = (path) => {
    switch (path) {
      case '/management/orders': return byCat.order ?? 0;
      case '/management/reviews': return byCat.review ?? 0;
      case '/management/catering': return byCat.catering ?? 0;
      case '/management/promotions': return byCat.promotion ?? 0;
      case '/management/announcements': return byCat.general ?? 0;
      case '/management': return (byCat.complaint ?? 0) + (byCat.general ?? 0) + (byCat.favorite ?? 0);
      default: return 0;
    }
  };

  const menuItems = [
    { path: '/management', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/management/orders', icon: ShoppingBag, label: 'Commandes' },
    { path: '/management/menu', icon: Utensils, label: 'Ma carte' },
    { path: '/management/grocery', icon: ShoppingBasket, label: 'Grocery shop' },
    { path: '/management/analytics', icon: BarChart3, label: 'Analyses' },
    { path: '/management/wallet', icon: Wallet, label: 'Portefeuille' },
    { path: '/management/employees', icon: Users, label: 'Employés' },
    { path: '/management/promotions', icon: Gift, label: 'Promotions' },
    { path: '/management/catering', icon: ChefHat, label: 'Traiteur' },
    { path: '/management/announcements', icon: Megaphone, label: 'Annonces' },
    { path: '/management/settings', icon: Settings, label: 'Paramètres' }
  ].map((item) => ({ ...item, badge: getBadgeForPath(item.path) }));

  const handleLogout = () => {
    localStorage.removeItem('restaurantToken');
    localStorage.removeItem('restaurantEmail');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    contextLogout();
    navigate('/business/login', { replace: true });
  };

  // Fermer les dropdowns quand on clique à l'extérieur
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notification-dropdown')) {
        setShowNotifications(false);
      }
      if (showProfile && !event.target.closest('.profile-dropdown')) {
        setShowProfile(false);
      }
      if (showSearchResults && !event.target.closest('.search-dropdown')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showProfile, showSearchResults]);

  const isActive = (path) => {
    if (path === '/management') {
      return location.pathname === '/management' || location.pathname === '/management/';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className="dashboard-shell min-h-screen" style={{ background: '#FBF9D9' }}>
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col`}
        style={{ background: PRIMARY, color: '#FBF9D9', padding: '22px 18px', gap: '20px' }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '0 8px' }}>
          <span className="flex items-center gap-2.5">
            <span style={{ width: '32px', height: '32px', borderRadius: '999px', background: TERRA, display: 'grid', placeItems: 'center' }}>
              <span style={{ width: '16px', height: '9px', borderRadius: '0 0 999px 999px', background: '#FFC717' }} />
            </span>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '20px', letterSpacing: '-0.03em' }}>namke</span>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#FFC717', marginLeft: 'auto' }}>pro</span>
          </span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden"
            style={{ border: 'none', background: 'transparent', color: 'rgba(251,249,217,0.8)', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Sélecteur d'établissement */}
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(251,249,217,0.12)', borderRadius: '999px', padding: '7px 10px 7px 12px', flexShrink: 0 }}>
          <span style={{ flex: 'none', width: '30px', height: '30px', borderRadius: '999px', background: 'rgba(251,249,217,0.2)', display: 'grid', placeItems: 'center', fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 700, overflow: 'hidden' }}>
            {restaurantData?.profile?.banner_image_url || restaurantData?.image_url ? (
              <img
                src={restaurantData?.profile?.banner_image_url || restaurantData?.image_url}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              restaurantInitial
            )}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
            <strong style={{ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{restaurantData?.name || 'Mon restaurant'}</strong>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', color: 'rgba(251,249,217,0.65)' }}>{restaurantData?.city || '—'}</span>
          </span>
        </span>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto" style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '11px', width: '100%', textAlign: 'left',
                  fontSize: '14px', padding: '10px 14px', borderRadius: '999px', minHeight: '42px',
                  border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif',
                  background: active ? TERRA : 'transparent',
                  color: active ? '#FCFBF5' : 'rgba(251,249,217,0.86)',
                  fontWeight: active ? 600 : 400,
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(251,249,217,0.1)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon size={17} className="flex-shrink-0" />
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge > 0 && (
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 700, color: active ? '#FCFBF5' : '#FFC717' }}>
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Carte de contexte — service en cours */}
        <div style={{ marginTop: 'auto', flexShrink: 0, background: 'rgba(251,249,217,0.1)', borderRadius: '22px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#FFC717' }}>Service</span>
          <span style={{ fontSize: '13px', lineHeight: 1.5, color: 'rgba(251,249,217,0.82)' }}>
            {pendingOrdersCount > 0
              ? `${pendingOrdersCount} commande${pendingOrdersCount > 1 ? 's' : ''} en attente de traitement.`
              : 'Aucune commande en attente pour le moment.'}
          </span>
          <button
            onClick={() => { navigate('/management/orders'); setSidebarOpen(false); }}
            style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, color: '#FFC717' }}
          >
            Voir les commandes →
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, marginTop: '6px', fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(251,249,217,0.55)' }}
          >
            <LogOut size={13} />
            Déconnexion
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:ml-64 min-h-screen min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex-shrink-0" style={{ background: 'rgba(251,249,217,0.94)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(5,16,4,0.1)' }}>
          <div className="flex items-center gap-3" style={{ minHeight: '64px', padding: '10px 20px' }}>
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
              style={{ border: 'none', background: 'transparent', color: PRIMARY, cursor: 'pointer', padding: '6px' }}
            >
              <Menu size={20} />
            </button>

            {/* Search Bar */}
            <div className="flex-1 max-w-lg hidden md:block">
              <div className="relative search-dropdown">
                <label
                  className="flex items-center gap-2.5"
                  style={{ background: '#FCFBF5', borderRadius: '999px', padding: '0 18px', minHeight: '42px' }}
                >
                  <Search size={16} style={{ color: PRIMARY, flexShrink: 0 }} />
                  <input
                    type="text"
                    placeholder="Rechercher un plat, une commande..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowSearchResults(true);
                    }}
                    onFocus={() => setShowSearchResults(true)}
                    style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '14px', color: '#051004', outline: 'none' }}
                  />
                </label>

                {showSearchResults && searchTerm.trim().length >= 2 && (
                  <div className="absolute left-0 right-0 z-50 max-h-96 overflow-y-auto" style={{ marginTop: '8px', background: '#FCFBF5', borderRadius: '20px', boxShadow: '0 16px 32px rgba(5,16,4,0.14)', padding: '6px' }}>
                    {searchLoading && (
                      <div style={{ padding: '16px', fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Recherche...</div>
                    )}
                    {!searchLoading && !hasSearchResults && (
                      <div style={{ padding: '16px', fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucun résultat pour « {searchTerm} »</div>
                    )}
                    {!searchLoading && searchResults.dishes.length > 0 && (
                      <div style={{ borderBottom: searchResults.orders.length > 0 ? '1px solid rgba(5,16,4,0.08)' : 'none', paddingBottom: '4px' }}>
                        <div style={{ padding: '10px 14px 4px', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.45)' }}>Plats</div>
                        {searchResults.dishes.map((d) => (
                          <button
                            key={`dish-${d.id}`}
                            onClick={() => goToSearchResult('/management/menu')}
                            style={{ width: '100%', textAlign: 'left', padding: '9px 14px', borderRadius: '14px', fontSize: '14px', color: '#051004', border: 'none', background: 'transparent', cursor: 'pointer' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            {d.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {!searchLoading && searchResults.orders.length > 0 && (
                      <div style={{ paddingTop: '4px' }}>
                        <div style={{ padding: '10px 14px 4px', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.45)' }}>Commandes</div>
                        {searchResults.orders.map((o) => (
                          <button
                            key={`order-${o.id}`}
                            onClick={() => goToSearchResult('/management/orders')}
                            style={{ width: '100%', textAlign: 'left', padding: '9px 14px', borderRadius: '14px', fontSize: '14px', color: '#051004', border: 'none', background: 'transparent', cursor: 'pointer' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            Commande #{o.id ?? o.order_number}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <span className="hidden lg:inline-flex items-center gap-2" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '10px 18px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', whiteSpace: 'nowrap' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: pendingOrdersCount > 0 ? TERRA : '#72B744' }} />
              {pendingOrdersCount > 0 ? `${pendingOrdersCount} en attente` : 'Service à jour'}
            </span>

            {/* Right side actions */}
            <div className="flex items-center gap-2" style={{ marginLeft: 'auto' }}>
              {/* Notifications */}
              <div className="relative notification-dropdown">
                <button
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    if (!showNotifications && unreadCount > 0) {
                      markAllAsRead();
                    }
                  }}
                  style={{ position: 'relative', border: 'none', cursor: 'pointer', background: 'transparent', color: PRIMARY, width: '40px', height: '40px', borderRadius: '999px', display: 'grid', placeItems: 'center' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(114,183,68,0.14)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Bell size={19} />
                  {unreadCount > 0 && (
                    <span style={{ position: 'absolute', top: '2px', right: '2px', background: TERRA, color: '#FCFBF5', fontFamily: 'Archivo, sans-serif', fontSize: '10.5px', fontWeight: 700, borderRadius: '999px', minWidth: '18px', height: '18px', display: 'grid', placeItems: 'center', padding: '0 3px' }}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 z-50 flex flex-col" style={{ marginTop: '8px', width: '380px', maxWidth: 'calc(100vw - 24px)', maxHeight: '500px', background: '#FCFBF5', borderRadius: '24px', boxShadow: '0 20px 44px rgba(5,16,4,0.18)', overflow: 'hidden' }}>
                    <div className="flex items-center justify-between" style={{ padding: '18px 20px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
                      <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '18px' }}>Notifications</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="flex items-center gap-1"
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', color: 'rgba(5,16,4,0.55)' }}
                        >
                          <Check size={13} />
                          Tout marquer comme lu
                        </button>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {notificationsLoading ? (
                        <div style={{ padding: '32px', textAlign: 'center' }}>
                          <div className="animate-spin" style={{ width: '24px', height: '24px', borderRadius: '999px', border: '2px solid rgba(5,16,4,0.12)', borderTopColor: PRIMARY, margin: '0 auto' }} />
                        </div>
                      ) : notifications && notifications.length > 0 ? (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => {
                              if (!notification.is_read) {
                                markAsRead(notification.id);
                              }
                            }}
                            style={{ padding: '14px 20px', borderBottom: '1px solid rgba(5,16,4,0.06)', cursor: 'pointer', background: !notification.is_read ? 'rgba(114,183,68,0.08)' : 'transparent' }}
                          >
                            <div className="flex items-center gap-2" style={{ marginBottom: '4px' }}>
                              <p style={{ margin: 0, fontSize: '14px', fontWeight: !notification.is_read ? 600 : 500, color: '#051004' }}>
                                {notification.title}
                              </p>
                              {!notification.is_read && (
                                <span style={{ width: '7px', height: '7px', borderRadius: '999px', background: TERRA }} />
                              )}
                            </div>
                            <p style={{ margin: '0 0 8px', fontSize: '12.5px', color: 'rgba(5,16,4,0.6)' }}>{notification.message}</p>
                            <div className="flex items-center gap-1.5" style={{ fontSize: '11.5px', color: 'rgba(5,16,4,0.4)' }}>
                              <Clock size={11} />
                              {notification.created_at && (
                                <span>
                                  {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                          <Bell size={36} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 10px' }} />
                          <p style={{ margin: 0, fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucune notification</p>
                        </div>
                      )}
                    </div>
                    {notifications && notifications.length > 0 && (
                      <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
                        <button
                          onClick={() => {
                            setShowNotifications(false);
                            navigate('/management');
                          }}
                          style={{ width: '100%', textAlign: 'center', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13.5px', fontWeight: 600, color: PRIMARY }}
                        >
                          Voir toutes les notifications
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile */}
              <div className="relative profile-dropdown">
                <button
                  onClick={() => setShowProfile(!showProfile)}
                  className="flex items-center gap-2.5"
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px' }}
                >
                  <span style={{ width: '36px', height: '36px', borderRadius: '999px', background: '#72B744', display: 'grid', placeItems: 'center', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 700, color: '#051004' }}>
                    {restaurantInitial}
                  </span>
                  <span className="hidden md:block" style={{ fontSize: '13.5px', fontWeight: 600, color: '#051004', maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{accountName}</span>
                </button>

                {/* Profile dropdown */}
                {showProfile && (
                  <div className="absolute right-0 z-50" style={{ marginTop: '8px', width: '200px', background: '#FCFBF5', borderRadius: '20px', boxShadow: '0 20px 44px rgba(5,16,4,0.18)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#051004', overflow: 'hidden', textOverflow: 'ellipsis' }}>{accountName}</p>
                      {accountEmail && (
                        <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgba(5,16,4,0.5)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{accountEmail}</p>
                      )}
                    </div>
                    <div style={{ padding: '6px' }}>
                      <button onClick={() => { setShowProfile(false); navigate('/management/settings?section=profile'); }} style={{ width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: '14px', fontSize: '13.5px', color: '#051004', border: 'none', background: 'transparent', cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                        Mon profil
                      </button>
                      <button onClick={() => { setShowProfile(false); navigate('/management/settings?section=security'); }} style={{ width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: '14px', fontSize: '13.5px', color: '#051004', border: 'none', background: 'transparent', cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                        Paramètres
                      </button>
                      <button
                        onClick={handleLogout}
                        style={{ width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: '14px', fontSize: '13.5px', color: '#A32C0F', border: 'none', background: 'transparent', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF0EA'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children || <Outlet />}
        </main>
      </div>

      {/* Fond assombri derrière la sidebar ouverte sur mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(5,16,4,0.45)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default RestaurantLayout;
